import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaEnvelope,
  FaLock,
  FaSignInAlt,
  FaArrowRight,
  FaEye,
  FaEyeSlash,
  FaShieldAlt,
  FaExclamationTriangle,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import { useDispatch } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import { setUser } from "../store/slices/authSlice";
import { useFooter } from "../context/FooterContext";
// import GoogleLoginButton from "../components/auth/GoogleLoginButton"; // Commented for future use

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setShowFooter } = useFooter();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [googleWarning, setGoogleWarning] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Hide footer when component mounts, show when unmounts
  useEffect(() => {
    setShowFooter(false);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  // Check URL params for Google error/warning
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    const message = params.get("message");

    if (error === "no_account" && message) {
      const decodedMessage = decodeURIComponent(message);
      setGoogleWarning(decodedMessage);
      toast.error(decodedMessage);
      // Remove the error param from URL without reloading
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Check dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) setError("");
    if (googleWarning) setGoogleWarning("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        "/api/auth/login",
        formData,
      );

      localStorage.setItem("token", response.data.token);

      const userData = {
        ...response.data.user,
        _id: response.data.user.id || response.data.user._id,
      };

      dispatch(setUser(userData));
      toast.success("Login successful!");
      navigate("/dashboard");
    } catch (error) {
      console.error("Login error:", error);

      if (error.response?.data?.requiresVerification) {
        setError(
          "Please verify your email first. Check your inbox for verification link.",
        );
        toast.error("Please verify your email first");
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
        toast.error(error.response.data.message);
      } else {
        setError("Invalid email or password. Please try again.");
        toast.error("Invalid email or password");
      }
    } finally {
      setLoading(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  return (
    <div
      style={{
        minHeight: "calc(100vh - 200px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
        background: isDarkMode ? "#0f0f1a" : "#f8fafc",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated Background Particles */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        {[...Array(25)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.3, 0] }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              delay: Math.random() * 10,
            }}
            style={{
              position: "absolute",
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              width: Math.random() * 3 + 1,
              height: Math.random() * 3 + 1,
              background: `linear-gradient(135deg, #8b5cf6, #3b82f6)`,
              borderRadius: "50%",
              filter: "blur(2px)",
            }}
          />
        ))}
      </div>

      {/* Floating gradient orbs */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.1, 0.3],
        }}
        transition={{ duration: 8, repeat: Infinity }}
        style={{
          position: "absolute",
          top: "10%",
          right: "5%",
          width: 300,
          height: 300,
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />
      <motion.div
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.2, 0.1, 0.2],
        }}
        transition={{ duration: 10, repeat: Infinity, delay: 2 }}
        style={{
          position: "absolute",
          bottom: "10%",
          left: "5%",
          width: 250,
          height: 250,
          background:
            "radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{
          maxWidth: "460px",
          width: "100%",
          background: isDarkMode
            ? "rgba(30, 30, 45, 0.95)"
            : "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
          borderRadius: "32px",
          padding: "2.5rem",
          boxShadow: isDarkMode
            ? "0 25px 50px -12px rgba(0,0,0,0.5)"
            : "0 25px 50px -12px rgba(0,0,0,0.25)",
          border: `1px solid ${isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Animated gradient border top */}
        <motion.div
          animate={{
            background: [
              "linear-gradient(90deg, #8b5cf6, #3b82f6, #06b6d4, #8b5cf6)",
              "linear-gradient(90deg, #3b82f6, #06b6d4, #8b5cf6, #3b82f6)",
            ],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            borderRadius: "32px 32px 0 0",
          }}
        />

        <motion.div
          variants={itemVariants}
          style={{ textAlign: "center", marginBottom: "2rem" }}
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "1rem",
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                width: "60px",
                height: "60px",
                borderRadius: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <HiOutlineSparkles size={32} color="white" />
            </div>
          </motion.div>
          <h1
            style={{
              fontSize: "2rem",
              marginBottom: "0.5rem",
              background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontWeight: 700,
            }}
          >
            Welcome Back!
          </h1>
          <p style={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}>
            Login to access your account
          </p>
        </motion.div>

        {/* Error Message Display */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              style={{
                background: "rgba(239, 68, 68, 0.1)",
                color: "#ef4444",
                padding: "0.8rem 1rem",
                borderRadius: "16px",
                marginBottom: "1.5rem",
                fontSize: "0.85rem",
                textAlign: "center",
                border: "1px solid rgba(239, 68, 68, 0.3)",
              }}
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Google Warning Message Display */}
        <AnimatePresence>
          {googleWarning && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              style={{
                background: "rgba(245, 158, 11, 0.15)",
                color: "#f59e0b",
                padding: "1rem",
                borderRadius: "16px",
                marginBottom: "1.5rem",
                fontSize: "0.85rem",
                textAlign: "center",
                border: "1px solid rgba(245, 158, 11, 0.4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
              }}
            >
              <FaExclamationTriangle size={16} />
              {googleWarning}
            </motion.div>
          )}
        </AnimatePresence>

        <form onSubmit={handleSubmit}>
          <motion.div
            variants={itemVariants}
            style={{ marginBottom: "1.5rem" }}
          >
            <label
              style={{
                display: "block",
                marginBottom: "0.5rem",
                color: isDarkMode ? "#cbd5e1" : "#475569",
                fontWeight: 500,
                fontSize: "0.875rem",
              }}
            >
              <FaEnvelope style={{ marginRight: "0.5rem", color: "#8b5cf6" }} />
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter your email"
              style={{
                width: "100%",
                padding: "0.9rem 1rem",
                border: `2px solid ${error ? "#ef4444" : isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
                borderRadius: "16px",
                outline: "none",
                fontSize: "1rem",
                transition: "all 0.3s",
                backgroundColor: isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff",
                color: isDarkMode ? "#f1f5f9" : "#1e293b",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
              onBlur={(e) => {
                if (!error) {
                  e.target.style.borderColor = isDarkMode
                    ? "rgba(139, 92, 246, 0.2)"
                    : "rgba(139, 92, 246, 0.1)";
                }
              }}
            />
          </motion.div>

          <motion.div
            variants={itemVariants}
            style={{ marginBottom: "1.5rem" }}
          >
            <label
              style={{
                display: "block",
                marginBottom: "0.5rem",
                color: isDarkMode ? "#cbd5e1" : "#475569",
                fontWeight: 500,
                fontSize: "0.875rem",
              }}
            >
              <FaLock style={{ marginRight: "0.5rem", color: "#8b5cf6" }} />
              Password
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Enter your password"
                style={{
                  width: "100%",
                  padding: "0.9rem 1rem",
                  paddingRight: "3rem",
                  border: `2px solid ${error ? "#ef4444" : isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
                  borderRadius: "16px",
                  outline: "none",
                  fontSize: "1rem",
                  transition: "all 0.3s",
                  backgroundColor: isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff",
                  color: isDarkMode ? "#f1f5f9" : "#1e293b",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                onBlur={(e) => {
                  if (!error) {
                    e.target.style.borderColor = isDarkMode
                      ? "rgba(139, 92, 246, 0.2)"
                      : "rgba(139, 92, 246, 0.1)";
                  }
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: isDarkMode ? "#94a3b8" : "#64748b",
                }}
              >
                {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
              </button>
            </div>
          </motion.div>

          <motion.div
            variants={itemVariants}
            style={{ textAlign: "right", marginBottom: "1.5rem" }}
          >
            <Link
              to="/forgot-password"
              style={{
                color: "#8b5cf6",
                textDecoration: "none",
                fontSize: "0.875rem",
                fontWeight: 500,
                transition: "color 0.3s",
              }}
              onMouseEnter={(e) => (e.target.style.color = "#3b82f6")}
              onMouseLeave={(e) => (e.target.style.color = "#8b5cf6")}
            >
              Forgot Password?
            </Link>
          </motion.div>

          <motion.button
            variants={itemVariants}
            type="submit"
            disabled={loading}
            whileHover={{ scale: loading ? 1 : 1.02 }}
            whileTap={{ scale: loading ? 1 : 0.98 }}
            style={{
              width: "100%",
              padding: "1rem",
              background: loading
                ? "#94a3b8"
                : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              color: "white",
              border: "none",
              borderRadius: "16px",
              fontSize: "1rem",
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              transition: "all 0.3s",
              boxShadow: loading ? "none" : "0 4px 14px 0 rgba(139,92,246,0.4)",
            }}
          >
            <FaSignInAlt /> {loading ? "Logging in..." : "Login"}
          </motion.button>
        </form>

        {/* Divider and Google Login - Completely removed */}

        <motion.div variants={itemVariants} style={{ textAlign: "center" }}>
          <p
            style={{
              color: isDarkMode ? "#94a3b8" : "#64748b",
              fontSize: "0.875rem",
            }}
          >
            Don't have an account?{" "}
            <Link
              to="/register"
              style={{
                color: "#8b5cf6",
                textDecoration: "none",
                fontWeight: 600,
                transition: "color 0.3s",
              }}
              onMouseEnter={(e) => (e.target.style.color = "#3b82f6")}
              onMouseLeave={(e) => (e.target.style.color = "#8b5cf6")}
            >
              Create an account
              <FaArrowRight
                style={{
                  marginLeft: "0.3rem",
                  fontSize: "0.7rem",
                  display: "inline",
                }}
              />
            </Link>
          </p>
        </motion.div>

        {/* Security Badge */}
        <motion.div
          variants={itemVariants}
          style={{
            marginTop: "1.5rem",
            paddingTop: "1rem",
            borderTop: `1px solid ${isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
          }}
        >
          <FaShieldAlt size={12} color="#10b981" />
          <span
            style={{
              color: isDarkMode ? "#94a3b8" : "#64748b",
              fontSize: "0.7rem",
            }}
          >
            Secure login with 256-bit encryption
          </span>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Login;

